import { isBrowser } from '@utils/isBrowser';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useState } from 'react';

import { ShareButton } from './ShareButton';
import { ShareSocials } from './ShareSocials';

interface ShareProps {
  className?: string;
  onClick: (platform: string) => void;
  text: string;
  utmCampaign?: string;
  utmMedium?: string;
  utmSource?: string;
}

export const ShareButtons: FC<ShareProps> = ({
  className = 'flex items-center justify-center gap-2',
  onClick,
  text,
  utmCampaign = 'share',
  utmMedium = 'share-button',
  utmSource,
}) => {
  const { asPath } = useRouter();

  const [canShare, setCanShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [url, setUrl] = useState(
    `${asPath}?utm_campaign=${utmCampaign}&utm_medium=${utmMedium}`,
  );

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${url}&utm_source=clipboard`);
      onClick('clipboard');
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    } catch (err) {
      console.warn(err);

      setCopied(false);
    }
  }, [onClick, url]);

  const handleShare = useCallback(async () => {
    try {
      await navigator.share({
        title: text,
        url: `${url}&utm_source=clipboard`,
      });
      onClick('share');
    } catch (err) {
      console.warn(err);
    }
  }, [onClick, text, url]);

  useEffect(() => {
    setCanShare(typeof navigator?.share === 'function');

    if (isBrowser()) {
      const [pathname] = window.location.href.split('?');
      setUrl(`${pathname}?utm_campaign=${utmCampaign}&utm_medium=${utmMedium}`);
    }
  }, [utmCampaign, utmMedium, utmSource]);

  return (
    <div className={classNames(className)}>
      <ShareSocials onClick={onClick} text={text} url={url} />
      {canShare ? (
        <ShareButton
          icon="ic_share"
          label="Share link"
          onClick={() => void handleShare()}
        />
      ) : (
        <ShareButton
          icon={copied ? 'ic_check' : 'ic_link'}
          label="Copy link"
          onClick={() => void copyToClipboard()}
          variant={copied ? 'positive' : 'tertiary'}
        />
      )}
    </div>
  );
};
